import { Component, OnDestroy, OnInit , DestroyRef, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TableModule } from 'primeng/table';
import { Button } from 'primeng/button';
import { Tag } from 'primeng/tag';
import { Select } from 'primeng/select';
import { Tooltip } from 'primeng/tooltip';
import { MessageService } from 'primeng/api';
import { forkJoin, catchError, of } from 'rxjs';
import { firstValueFrom } from 'rxjs';
import { ProjectService } from '../../services/project.service';
import { EmployeeService } from '../../services/employee.service';
import { ClientService } from '../../services/client.service';
import { RoleService } from '../../services/role.service';
import { AuthService, UserInfo } from '../../services/auth.service';
import { PaymentTrackingItemService } from '../../services/payment-tracking-item.service';
import { Project } from '../../models/project.model';
import { Employee } from '../../models/employee.model';
import { Client } from '../../models/client.model';
import { Role } from '../../models/role.model';
import { PaymentTrackingComponent } from '../payment-tracking/payment-tracking.component';
import { WorkNotesComponent } from '../work-notes/work-notes.component';
import { PageHeaderSearchActionService } from '../../services/page-header-search-action.service';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';

interface ProjectWithInvoiced extends Project {
  totalInvoicedPercentage?: number;
}

@Component({
  selector: 'app-invoicing-plan',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    TableModule,
    Button,
    Tag,
    Select,
    Tooltip,
    PaymentTrackingComponent,
    WorkNotesComponent
  ],
  templateUrl: './invoicing-plan.component.html',
  styleUrl: './invoicing-plan.component.scss',
  providers: [MessageService]
})
export class InvoicingPlanComponent implements OnInit, OnDestroy {
  private destroyRef = inject(DestroyRef);
  projects: ProjectWithInvoiced[] = [];
  filteredProjects: ProjectWithInvoiced[] = [];
  employees: Employee[] = [];
  clients: Client[] = [];
  roles: Role[] = [];
  user: UserInfo | null = null;
  currentUserEmployee: Employee | null = null;
  isAccountManager: boolean = false;

  loading: boolean = true;
  searchText: string = '';
  expandedRows: { [key: string]: boolean } = {};
  
  // Query parameter filters
  filterDate: string | null = null;
  filterProjectIds: string[] = [];

  // Filter properties
  selectedStatusFilter: string | null = null;
  selectedProgressFilter: string | null = null;
  selectedAccountManagerFilter: string | null = null;
  selectedManagerFilter: string | null = null;

  // Filter options
  statusFilterOptions: { label: string; value: string | null }[] = [
    { label: 'All', value: null },
    { label: 'Green', value: 'Green' },
    { label: 'Amber', value: 'Amber' },
    { label: 'Red', value: 'Red' }
  ];

  progressFilterOptions: { label: string; value: string | null }[] = [
    { label: 'All', value: null },
    { label: 'To Start', value: 'To Start' },
    { label: 'On Track', value: 'On Track' },
    { label: 'Off Track', value: 'Off Track' },
    { label: 'Blocker', value: 'Blocker' },
    { label: 'On Hold', value: 'On Hold' },
    { label: 'Closed', value: 'Closed' }
  ];

  accountManagerFilterOptions: { label: string; value: string | null }[] = [];
  managerFilterOptions: { label: string; value: string | null }[] = [];
  serviceGroupFilterOptions: { label: string; value: string | null }[] = [];
  clientFilterOptions: { label: string; value: string | null }[] = [];

  selectedServiceGroupFilter: string | null = null;
  selectedClientFilter: string | null = null;

  constructor(
    private projectService: ProjectService,
    private employeeService: EmployeeService,
    private clientService: ClientService,
    private roleService: RoleService,
    private authService: AuthService,
    private paymentTrackingService: PaymentTrackingItemService,
    private messageService: MessageService,
    private route: ActivatedRoute,
    private router: Router,
    public pageHeaderService: PageHeaderSearchActionService
  ) { }

  ngOnInit() {
    // Header integration: search only
    this.pageHeaderService.setCanShowFilterToggle(true);
    this.pageHeaderService.setPlaceholder('Search by project, client, PM, Account Manager...');
    this.pageHeaderService.setCanShowExport(true);
    this.pageHeaderService.setCanShowPrimaryAction(false);
    this.pageHeaderService.setExportDisabled(false);
    this.pageHeaderService.searchText
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((text) => {
        this.searchText = text ?? '';
        this.onSearch();
      });
    this.pageHeaderService.exportClick
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.exportToExcel());

    this.authService.user$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(user => {
      this.user = user;
      if (user) {
        // Check for query parameters
        this.route.queryParams.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(params => {
          if (params['date'] && params['projects']) {
            // Filter projects based on query params
            const projectIds = params['projects'].split(',');
            const filterDate = params['date'];
            this.filterByDateAndProjects(filterDate, projectIds);
          } else {
            // Clear filters if no query params
            this.filterDate = null;
            this.filterProjectIds = [];
          }
          this.loadData();
        });
      }
    });
  }

  ngOnDestroy(): void {
    this.pageHeaderService.clearPageConfig();
  }

  filterByDateAndProjects(date: string, projectIds: string[]) {
    this.filterDate = date;
    this.filterProjectIds = projectIds;
  }

  loadData() {
    this.loading = true;
    
    const rolesObservable = this.roleService.getAllRoles().pipe(
      catchError(error => {
        return of([] as Role[]);
      })
    );
    
    forkJoin({
      projects: this.projectService.getAllProjects().pipe(
        catchError(error => {
          if (error.status !== 403) {
            console.error('Error loading projects:', error);
          }
          return of([] as Project[]);
        })
      ),
      employees: this.employeeService.getAllEmployees().pipe(
        catchError(error => {
          if (error.status !== 403) {
            console.error('Error loading employees:', error);
          }
          return of([] as Employee[]);
        })
      ),
      clients: this.clientService.getAllClients().pipe(
        catchError(error => {
          if (error.status !== 403) {
            console.error('Error loading clients:', error);
          }
          return of([] as Client[]);
        })
      ),
      roles: rolesObservable
    }).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: async (data) => {
        this.projects = (data.projects || []).filter(p => p.isActive);
        this.employees = data.employees || [];
        this.clients = (data.clients || []).filter(c => c.isActive);
        this.roles = data.roles || [];
        
        // Build filter options
        this.buildFilterOptions();
        
        // Find current user's employee record
        if (this.user?.email) {
          this.currentUserEmployee = this.employees.find(
            e => e.email?.toLowerCase() === this.user!.email.toLowerCase()
          ) || null;
          
          if (this.currentUserEmployee) {
            if (this.currentUserEmployee.roleTitle) {
              this.isAccountManager = this.currentUserEmployee.roleTitle.toLowerCase().includes('account manager');
            } else if (this.currentUserEmployee.roleId) {
              const accountManagerRole = this.roles.find(
                r => r.title?.toLowerCase().includes('account manager')
              );
              if (accountManagerRole) {
                this.isAccountManager = this.currentUserEmployee.roleId === accountManagerRole.id;
              }
            }
          }
        }
        
        // Load total invoiced percentage for each project from backend
        await this.loadInvoicedPercentages();
        
        // Apply role-based filtering
        this.applyRoleBasedFilter();
        this.loading = false;
      },
      error: (error) => {
        console.error('Error loading data:', error);
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to load projects. Please refresh the page or try again later.'
        });
        this.loading = false;
      }
    });
  }

  async loadInvoicedPercentages() {
    const percentagePromises = this.projects.map(async (project) => {
      try {
        const response = await firstValueFrom(
          this.paymentTrackingService.getTotalInvoicedPercentage(project.id).pipe(
            catchError(error => {
              console.error(`Error loading total invoiced percentage for project ${project.id}:`, error);
              return of({ totalInvoicedPercentage: 0 });
            })
          )
        );
        project.totalInvoicedPercentage = response?.totalInvoicedPercentage || 0;
      } catch (error) {
        console.error(`Error loading total invoiced percentage for project ${project.id}:`, error);
        project.totalInvoicedPercentage = 0;
      }
    });
    
    await Promise.all(percentagePromises);
  }

  buildFilterOptions() {
    const accountManagerRole = this.roles.find(r =>
      r.title?.toLowerCase().includes('account manager')
    );
    const accountManagers = accountManagerRole
      ? this.employees.filter(e => e.isActive && e.roleId === accountManagerRole.id)
      : [];

    this.accountManagerFilterOptions = [
      { label: 'All', value: null },
      ...accountManagers.map(am => ({
        label: am.name || am.email || 'Unknown',
        value: am.id
      }))
    ];

    // Match the role title exactly ('pm' as a substring also matches "Develop-pm-ent", which put
    // Learning & Development staff in this list), and collect EVERY matching role rather than the
    // first - roles come back unordered, so find() could latch onto the wrong one.
    const pmRoleIds = this.roles
      .filter(r => {
        const title = r.title?.toLowerCase().trim();
        return title === 'project manager' || title === 'pm';
      })
      .map(r => r.id);

    const projectManagers = pmRoleIds.length
      ? this.employees.filter(e => e.isActive && (
        (e.roleIds && e.roleIds.some(id => pmRoleIds.includes(id))) ||
        (e.roleId && pmRoleIds.includes(e.roleId))
      ))
      : [];

    this.managerFilterOptions = [
      { label: 'All', value: null },
      ...projectManagers
        .map(pm => ({
          label: pm.name || pm.email || 'Unknown',
          value: pm.id
        }))
        .sort((a, b) => a.label.localeCompare(b.label))
    ];

    const uniqueServiceGroups = [...new Map(
      this.projects.filter(p => p.serviceGroupId && p.serviceGroupName)
        .map(p => [p.serviceGroupId!, p.serviceGroupName!])
    ).entries()].sort((a, b) => a[1].localeCompare(b[1]));
    this.serviceGroupFilterOptions = [
      { label: 'All', value: null },
      ...uniqueServiceGroups.map(([id, name]) => ({ label: name, value: id }))
    ];

    const uniqueClients = [...new Map(
      this.projects.filter(p => p.clientId && p.clientName)
        .map(p => [p.clientId!, p.clientName!])
    ).entries()].sort((a, b) => a[1].localeCompare(b[1]));
    this.clientFilterOptions = [
      { label: 'All', value: null },
      ...uniqueClients.map(([id, name]) => ({ label: name, value: id }))
    ];
  }

  // Sales has read-only visibility: view + expand + export, but no payment-tracking writes.
  get isSalesReadonly(): boolean {
    return this.user?.isSales === true && !this.user?.isAdmin && !this.user?.isPM && !this.user?.isFinance;
  }

  applyRoleBasedFilter() {
    if (!this.user) {
      this.filteredProjects = [];
      return;
    }

    let filtered = [...this.projects];

    if (this.user.isAdmin || this.user.isFinance || this.isAccountManager || this.user.isSales) {
      // No filtering needed - see all projects (Sales has read-only visibility)
    } else if (this.user.isPM) {
      // PM sees only their own projects
      if (this.currentUserEmployee) {
        filtered = filtered.filter(p => p.managerId === this.currentUserEmployee!.id);
      } else {
        filtered = [];
      }
    } else {
      filtered = [];
    }

    // Apply search filter
    if (this.searchText.trim() !== '') {
      const searchLower = this.searchText.toLowerCase();
      filtered = filtered.filter(proj =>
        proj.name?.toLowerCase().includes(searchLower) ||
        proj.clientName?.toLowerCase().includes(searchLower) ||
        proj.managerName?.toLowerCase().includes(searchLower) ||
        proj.accountManagerName?.toLowerCase().includes(searchLower)
      );
    }

    // Apply status filter (Amber matches both 'Amber' and legacy 'Yellow')
    if (this.selectedStatusFilter !== null) {
      filtered = filtered.filter(p =>
        p.status === this.selectedStatusFilter ||
        (this.selectedStatusFilter === 'Amber' && p.status === 'Yellow')
      );
    }

    // Apply progress filter
    if (this.selectedProgressFilter !== null) {
      filtered = filtered.filter(p => p.progress === this.selectedProgressFilter);
    }

    // Apply account manager filter
    if (this.selectedAccountManagerFilter !== null) {
      filtered = filtered.filter(p => p.accountManagerId === this.selectedAccountManagerFilter);
    }

    // Apply manager filter
    if (this.selectedManagerFilter !== null) {
      filtered = filtered.filter(p => p.managerId === this.selectedManagerFilter);
    }

    // Apply service group filter
    if (this.selectedServiceGroupFilter !== null) {
      filtered = filtered.filter(p => p.serviceGroupId === this.selectedServiceGroupFilter);
    }

    // Apply client filter
    if (this.selectedClientFilter !== null) {
      filtered = filtered.filter(p => p.clientId === this.selectedClientFilter);
    }

    // Apply query parameter filters (from invoice planning grid)
    if (this.filterProjectIds.length > 0) {
      filtered = filtered.filter(p => this.filterProjectIds.includes(p.id));
      // Auto-expand filtered projects
      filtered.forEach(project => {
        this.expandedRows[project.id] = true;
      });
    }

    this.filteredProjects = filtered;
  }

  onStatusFilterChange() {
    this.applyRoleBasedFilter();
  }

  onProgressFilterChange() {
    this.applyRoleBasedFilter();
  }

  onAccountManagerFilterChange() {
    this.applyRoleBasedFilter();
  }

  onManagerFilterChange() {
    this.applyRoleBasedFilter();
  }

  onServiceGroupFilterChange() {
    this.applyRoleBasedFilter();
  }

  onClientFilterChange() {
    this.applyRoleBasedFilter();
  }

  clearFilters() {
    this.searchText = '';
    this.pageHeaderService.setSearchText('');
    this.selectedStatusFilter = null;
    this.selectedProgressFilter = null;
    this.selectedAccountManagerFilter = null;
    this.selectedManagerFilter = null;
    this.selectedServiceGroupFilter = null;
    this.selectedClientFilter = null;
    this.applyRoleBasedFilter();
  }

  hasActiveFilters(): boolean {
    return !!(this.searchText.trim() ||
      this.selectedStatusFilter ||
      this.selectedProgressFilter ||
      this.selectedAccountManagerFilter ||
      this.selectedManagerFilter ||
      this.selectedServiceGroupFilter ||
      this.selectedClientFilter);
  }

  onSearch() {
    this.applyRoleBasedFilter();
  }

  clearSearch() {
    this.searchText = '';
    this.pageHeaderService.setSearchText('');
    this.applyRoleBasedFilter();
  }

  toggleRow(project: ProjectWithInvoiced) {
    this.expandedRows[project.id] = !this.expandedRows[project.id];
  }

  isRowExpanded(project: ProjectWithInvoiced): boolean {
    return !!this.expandedRows[project.id];
  }

  getStatusSeverity(status?: string): 'success' | 'warn' | 'danger' | 'secondary' {
    if (!status) return 'secondary';
    const statusLower = status.toLowerCase();
    if (statusLower === 'green') return 'success';
    if (statusLower === 'amber' || statusLower === 'yellow') return 'warn';
    if (statusLower === 'red') return 'danger';
    return 'secondary';
  }

  getProgressSeverity(progress?: string): 'success' | 'warn' | 'danger' | 'secondary' {
    if (!progress) return 'secondary';
    const progressLower = progress.toLowerCase();
    if (progressLower.includes('on track') || progressLower.includes('completed')) return 'success';
    if (progressLower.includes('on hold') || progressLower.includes('delayed')) return 'warn';
    return 'secondary';
  }

  formatDate(dateString?: string): string {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      weekday: 'short', 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    });
  }

  formatPercentage(percentage?: number): string {
    if (percentage === undefined || percentage === null) return '0%';
    return `${percentage.toFixed(2)}%`;
  }

  get totalProjectsCount(): number { return this.projects.length; }
  get showingCount(): number { return this.filteredProjects.length; }
  get greenCount(): number { return this.projects.filter(p => p.status === 'Green').length; }
  get amberRedCount(): number {
    return this.projects.filter(p =>
      p.status === 'Amber' || p.status === 'Yellow' || p.status === 'Red'
    ).length;
  }

  private exportToExcel(): void {
    const data = this.filteredProjects.map((p, i) => ({
      '#': i + 1,
      'Project Name': p.name || '',
      'Project Code': p.projectCode || '',
      'Project Manager': p.managerName || '',
      'Account Manager': p.accountManagerName || '',
      'Client': p.clientName || '',
      'Service Group': p.serviceGroupName || '',
      'Status': p.status || '',
      'Start Date': this.formatDate(p.startDate),
      'End Date': this.formatDate(p.endDate),
      'Invoiced %': p.totalInvoicedPercentage != null ? `${p.totalInvoicedPercentage.toFixed(2)}%` : '0%',
    }));
    const ws = XLSX.utils.json_to_sheet(data);
    ws['!cols'] = [
      { wch: 4 }, { wch: 36 }, { wch: 13 }, { wch: 24 }, { wch: 24 },
      { wch: 22 }, { wch: 22 }, { wch: 10 }, { wch: 12 }, { wch: 12 }, { wch: 12 }
    ];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Invoicing Plan');
    const buf = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    saveAs(new Blob([buf], { type: 'application/octet-stream' }), `invoicing_plan_${new Date().toISOString().slice(0, 10)}.xlsx`);
  }
}
